import { useEffect, useState } from "react";
import { getAccountCompliancesAsync } from "./../../../../services/complianceService";
import { AiFillStar, AiOutlineStar } from "react-icons/ai";
import { Link } from "react-router-dom";
import {
  IoBuildOutline,
  IoSettingsOutline,
  IoChevronForward,
} from "react-icons/io5";
import Modal from "./../../../../components/common/Modal";
import moment from "moment";
import ComplianceForm from "./components/ComplianceForm";
import AccountCompliances from "./components/AccountCompliances";

const Compliances = ({ id, account, onRefresh }) => {
  const [compliances, setCompliances] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showCompliances, setShowCompliances] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchCompliances = async () => {
      setIsLoading(true);

      const response = await getAccountCompliancesAsync(id);
      const { result } = response;

      if (mounted) {
        setCompliances(result);
        setIsLoading(false);
      }
    };

    fetchCompliances();

    return () => {
      mounted = false;
    };
  }, [id, refresh]);

  const renderIcon = (data) => {
    let icon = <AiFillStar className="text-yellow-400 text-xs mr-4" />;
    if (!data) icon = <AiOutlineStar className="text-gray-400 text-xs mr-4" />;

    return icon;
  };

  const renderStatus = (data) => {
    const {
      compliance_status: status,
      compliance_status_type: type,
      compliance_status_text: prefix,
      valid_from,
      valid_to,
      due_date,
    } = data;

    let status_content = (
      <span className="text-sm text-gray-400">Not available</span>
    );

    if (status === 1) {
      if (valid_from) {
        status_content = (
          <span className="text-sm text-green-500">
            Valid from {moment(valid_from).format("DD-MMM-yyyy")} to{" "}
            {moment(valid_to).format("DD-MMM-yyyy")}
          </span>
        );
      } else {
        status_content = <span className="text-sm text-green-500">{type}</span>;
      }
    } else if (status === 2) {
      if (due_date) {
        status_content = (
          <span className="text-sm text-yellow-500">
            {prefix} {moment(due_date).format("LL")}
          </span>
        );
      } else {
        status_content = (
          <span className="text-sm text-yellow-500">{type}</span>
        );
      }
    } else if (status === 3) {
      if (due_date) {
        status_content = (
          <span className="text-sm text-red-500">
            {prefix} {moment(due_date).format("LL")}
          </span>
        );
      } else {
        status_content = <span className="text-sm text-red-500">{type}</span>;
      }
    }

    return status_content;
  };

  const handleModal = (data) => {
    setShowForm(!showForm);
    if (!data) return setCompliance(null);

    setCompliance(data);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setRefresh(!refresh);
    onRefresh();
  };

  const handleCompliancesSuccess = () => {
    setShowCompliances(false);
    setRefresh(!refresh);
  };

  const compliance_items = [
    {
      name: "S2 License",
      description: "License Number, Registration Date, License Validity, etc.",
      url: `/accounts/${id}/s2-licenses`,
    },
    {
      name: "Professional Tax Receipts",
      description: "PTR Number, Registration Date, Municipality, etc.",
      url: `/accounts/${id}/professional-tax-receipts`,
    },
    {
      name: "Sworn Declaration",
      description: "Year, Deadline, Documents, etc.",
      url: `/accounts/${id}/sworn-declarations`,
    },
  ];

  return (
    <div className="card p-0">
      <div className="p-8">
        <div className="mb-10">
          <p className="text-gray-400 text-sm">Documents</p>
          <span className="text-gray-400 text-xs">Account Documents</span>
        </div>
        <div className="space-y-1">
          {compliance_items.map((item, index) => (
            <Link
              key={index}
              to={item.url}
              className="flex justify-between items-center p-4 rounded-lg cursor-pointer hover:bg-gray-100"
            >
              <div>
                <div className="text-gray-500 text-sm">{item.name}</div>
                <span className="text-xs text-gray-400">
                  {item.description}
                </span>
              </div>
              <IoChevronForward className="text-blue-400" />
            </Link>
          ))}
        </div>
        <div className="block sm:flex justify-between items-center mt-10">
          <div>
            <p className="text-gray-400 text-sm">Compliances</p>
            <span className="text-gray-400 text-xs">Compliance Management</span>
          </div>
          <button onClick={() => setShowCompliances(true)}>
            <IoSettingsOutline className="text-blue-500" />
          </button>
        </div>
      </div>
      {isLoading && (
        <div>
          <div className="flex-1 space-y-2 animate-pulse px-6 py-5 border-b border-t">
            <div className="h-4 bg-gray-100 rounded w-3/5"></div>
            <div className="h-4 bg-gray-100 rounded w-4/5"></div>
          </div>
          <div className="flex-1 space-y-2 animate-pulse px-6 py-5 border-b">
            <div className="h-4 bg-gray-100 rounded w-3/5"></div>
            <div className="h-4 bg-gray-100 rounded w-4/5"></div>
          </div>
          <div className="flex-1 space-y-2 animate-pulse px-6 py-5 border-b">
            <div className="h-4 bg-gray-100 rounded w-3/5"></div>
            <div className="h-4 bg-gray-100 rounded w-4/5"></div>
          </div>
          <div className="flex-1 space-y-2 animate-pulse px-6 py-5 border-b">
            <div className="h-4 bg-gray-100 rounded w-3/5"></div>
            <div className="h-4 bg-gray-100 rounded w-4/5"></div>
          </div>
        </div>
      )}
      {!isLoading &&
        compliances.map((item, key) => (
          <div
            key={key}
            className={`${
              key % 2 === 0 ? "bg-gray-50" : "bg-white"
            } flex justify-between items-center px-6 py-5 group hover:bg-gray-700 last:rounded-lg`}
          >
            <div className="flex items-center">
              {renderIcon(item.is_compliance_active)}
              <div>
                <p className="text-gray-400 text-sm">{item.service_name}</p>
                {renderStatus(item)}
              </div>
            </div>
            <div className="opacity-0 group-hover:opacity-100">
              {item.is_compliance_active && (
                <button
                  className="text-sm text-blue-400 flex items-center max-w-sm"
                  onClick={() => handleModal(item)}
                >
                  <IoBuildOutline className="mr-2" />
                  Status
                </button>
              )}
            </div>
          </div>
        ))}
      <Modal
        size="md"
        title={compliance && compliance.service_name}
        isLoading={isFormLoading}
        isOpen={showForm}
        onClose={() => handleModal()}
        borderTop
      >
        <ComplianceForm
          id={id}
          data={compliance}
          isLoading={isFormLoading}
          setLoading={setIsFormLoading}
          onSuccess={handleSuccess}
        />
      </Modal>
      <Modal
        title="Account Compliances"
        isOpen={showCompliances}
        isLoading={isUpdating}
        onClose={() => setShowCompliances(false)}
        borderTop
      >
        <AccountCompliances
          id={id}
          data={compliances}
          isLoading={isUpdating}
          setLoading={setIsUpdating}
          onSuccess={handleCompliancesSuccess}
        />
      </Modal>
    </div>
  );
};

export default Compliances;
